import { useCallback, useMemo, useState } from 'react';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLabel } from 'victory';
import { darkenColor, getProfitColor } from '../../../../../helpers/colors';
import {
  formatBalance,
  formatPercentage,
} from '../../../../../helpers/NumberFormatter';
import { SortingToggleOptions } from '../../../types';
import { getBotColor } from '../helpers';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../../store/slices/subscription';
import { useSelector } from 'react-redux';
import { selectOverviewChartPercentage } from '../../../../../store/slices/ui';

type DataPoint = {
  x: string;
  y: number;
  value?: number;
  subscriptionId: number;
};
interface Props {
  data: Array<DataPoint>;
  activeToggleOption: SortingToggleOptions;
  showAllData: boolean | undefined;
  width: number;
  height: number;
}

interface CallbackArgs {
  active?: boolean;
  data?: any[];
  datum?: any;
  horizontal?: boolean;
  index?: any;
  x?: number;
  y?: number;
  scale?: any;
  tick?: any;
  ticks?: any;
  text?: any;
}

const getDiversityColorScale = (datum: DataPoint) => getBotColor(datum.x);

const getProfitColorScale = (datum: DataPoint) =>
  getProfitColor(datum.value ?? datum.y);

const xAxisStyle = {
  axis: { stroke: 'white' },
  ticks: { stroke: 'white', size: 3 },
  tickLabels: { fontSize: 10, fill: 'white', fontWeight: 700, opacity: 0.85 },
};

const yAxisStyle = {
  axis: { stroke: 'white' },
  ticks: { stroke: 'white', size: 3 },
  tickLabels: { fontSize: 12, fill: 'white', fontWeight: 700, opacity: 0.85 },
};

const BarChart = ({
  data,
  activeToggleOption,
  showAllData,
  width,
  height,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isRelative = useSelector(selectOverviewChartPercentage);

  // State to track the hovered bar's index
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const domain: { y: [number, number] } = useMemo(() => {
    const max = Math.max(...data.map((d) => d.y));
    return { y: [0, max + max * 0.2] };
  }, [data]);

  const getBarColor = useCallback(
    (datum: DataPoint) =>
      activeToggleOption === SortingToggleOptions.Diversity ||
      activeToggleOption === SortingToggleOptions.TotalValue
        ? getDiversityColorScale(datum)
        : getProfitColorScale(datum),
    [activeToggleOption],
  );

  const barStyle = useMemo(
    () => ({
      data: {
        fill: ({ datum }: any) => getBarColor(datum),
      },
    }),
    [getBarColor],
  );

  const handleXAxisTickPosition = useCallback(
    ({ index }: CallbackArgs) => {
      return typeof index === 'number'
        ? data.length > 8
          ? index % 2 === 0
            ? -8
            : 2
          : -8
        : 0;
    },
    [data],
  );

  const handleTickFormat = useCallback(
    (tick: number) =>
      isRelative ? formatPercentage(tick, 2) : formatBalance(tick, true),
    [isRelative],
  );

  const domainPadding = useMemo(() => ({ x: 380 / (data.length * 2) }), [data]);

  return (
    <VictoryChart
      key={`${isRelative}`}
      domain={domain}
      domainPadding={domainPadding}
      width={width * (showAllData ? 1.4 : 1)}
      height={height * (showAllData ? 1.4 : 1)}
    >
      <VictoryBar
        data={data}
        style={barStyle}
        cornerRadius={{ top: 3 }}
        events={[
          {
            target: 'data',
            eventHandlers: {
              onClick: () => {
                return [
                  {
                    target: 'data',
                    mutation: async ({ datum }) => {
                      if (!showAllData) {
                        return;
                      }

                      void dispatch(
                        setActiveSubscriptionId(datum.subscriptionId),
                      );
                      navigate('/sub');
                      return null; // No visual mutation on click
                    },
                  },
                ];
              },
              onMouseOver: () => {
                return [
                  {
                    target: 'data',
                    mutation: ({ datum, index }) => {
                      setHoveredIndex(index); // Track hovered bar

                      return {
                        style: { fill: darkenColor(getBarColor(datum)) },
                      };
                    },
                  },
                ];
              },
              onMouseOut: () => {
                return [
                  {
                    target: 'data',
                    mutation: ({ datum }) => {
                      setHoveredIndex(null); // Track hovered bar

                      return {
                        style: { fill: getBarColor(datum) },
                      };
                    },
                  },
                ];
              },
            },
          },
        ]}
      />
      <VictoryAxis
        crossAxis
        standalone={false}
        style={xAxisStyle}
        tickLabelComponent={<VictoryLabel dy={handleXAxisTickPosition} />}
        tickFormat={(tick: number, index: number) =>
          // Show only the label for the hovered bar
          hoveredIndex === null || hoveredIndex === index ? tick : ''
        }
      />
      <VictoryAxis
        dependentAxis
        crossAxis
        standalone={false}
        style={yAxisStyle}
        tickFormat={handleTickFormat}
        tickLabelComponent={<VictoryLabel dx={5} />}
      />
    </VictoryChart>
  );
};

export default BarChart;
